/** @format */
import { useAuthContext } from '@mpb-com/auth-sdk/client';
import { useEffect, useRef } from 'react';

import { IdentityKind } from './config/constants';
import {
    type AuthSideEffectsProps,
    type OnAnonymousOptions,
} from './config/types';
import { getIdentity, getIdentityKey } from './helpers';

export type { OnAnonymousOptions };

export const AuthSideEffects = ({
    onLoggedIn,
    onOAuthLoggedIn,
    onAnonymous,
}: AuthSideEffectsProps): null => {
    const { isInitialized, isLoggedIn, user, userContext } = useAuthContext();
    const previousKeyRef = useRef<string | null>(null);

    useEffect(() => {
        if (!isInitialized) return;

        const identity = getIdentity(isLoggedIn, user, userContext);

        if (identity === null) return;

        const key = getIdentityKey(identity);

        if (key === previousKeyRef.current) return;

        const wasLoggedIn = previousKeyRef.current !== null;
        previousKeyRef.current = key;

        switch (identity.kind) {
            case IdentityKind.CUSTOMER:
                onLoggedIn(identity.customerId);
                break;
            case IdentityKind.OAUTH:
                onOAuthLoggedIn?.(identity.userContext);
                break;
            case IdentityKind.ANONYMOUS:
                onAnonymous({ wasLoggedIn });
                break;
            default: {
                const exhaustiveCheck: never = identity;

                return exhaustiveCheck;
            }
        }
    }, [
        isInitialized,
        isLoggedIn,
        user,
        userContext,
        onLoggedIn,
        onOAuthLoggedIn,
        onAnonymous,
    ]);

    return null;
};
